'use client';

/**
 * DemoAskRendererSlotCard — mode8's polymorphic response slot.
 *
 * Same dispatched-card-slot pattern Reception's `WebQARendererSlotCard`
 * uses. Reads `useDemoAskStore.rendererCard` (auto-injected by the
 * gateway from BFF_REGISTRY) and mounts the named card from
 * CANVAS_CARD_REGISTRY when set.
 *
 * Without a renderer_card hint, falls back to a structured readout of
 * the AskResponse — status, intent, simulation_id, project. Enough to
 * verify the round-trip works end-to-end; later iterations replace this
 * with richer dispatched cards (Glass Box bound to simulation_id, search
 * candidate list for status='search', etc.).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { LayoutDashboard } from 'lucide-react';
import { useIsFetching } from '@tanstack/react-query';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { CanvasCardShell } from '../canvas-card-shell';
import { QA_SECTION } from './web-qa-chrome';
import { DemoTestRunHeaderCard } from './demo-test-run-header-card';
import { DemoSceneProgressionCard } from './demo-scene-progression-card';
import { DemoTokenomicsCard } from './demo-tokenomics-card';
import { DemoWeightsCard } from './demo-weights-card';
import { DemoRulesCard } from './demo-rules-card';
import { DemoFrontierCard } from './demo-frontier-card';
import { DemoRecordingCard } from './demo-recording-card';
import { DemoExtractedLinesCard } from './demo-extracted-lines-card';
import { DemoAskTranscriptCard } from './demo-ask-transcript-card';


export function DemoAskRendererSlotCard() {
  const T = useCardTypography();
  const response = useDemoAskStore((s) => s.response);
  const turns = useDemoAskStore((s) => s.turns);
  const testRun = useDemoAskStore((s) => s.testRun);
  const isLoading = useDemoAskStore((s) => s.isLoading);

  // Loading state — the input card writes isLoading while either ki_ask or
  // test_runner is in flight.
  if (isLoading) {
    return (
      <section className={QA_SECTION}>
        <div className="w-full">
          <div className="rounded-2xl border border-dashed border-border/40 bg-muted/20 px-8 py-12 text-center">
            <p className={cn(T.metadata, 'text-muted-foreground/70')}>Working…</p>
          </div>
        </div>
      </section>
    );
  }

  // test_runner response — composed from atomic slice cards. Each card
  // reads its slice from the test_run_view BFF (atom) and/or
  // tokenomics_rollup BFF (rollup). React Query dedupes the test_run_view
  // fetch across the header + scene progression cards.
  if (testRun) {
    return <TestRunCanvas />;
  }

  // Empty state — no conversation started, no test run pending. Same
  // shape Reception uses.
  if (turns.length === 0) {
    return (
      <section className={QA_SECTION}>
        <div className="w-full">
          <div className="rounded-2xl border border-dashed border-border/40 bg-muted/20 px-8 py-12 text-center">
            <LayoutDashboard className="w-6 h-6 mx-auto mb-3 text-muted-foreground/40" />
            <p className={cn(T.metadata, 'text-muted-foreground/70')}>
              Ask about an entity above, or run the canary test. The agent's response —
              simulation, clarification, candidate search, or test outcome — lands here.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Conversation has turns — mount the transcript card. It owns stacking,
  // per-turn TurnContext, and the narrative/data-card pairing for each
  // turn. The slot stays thin: dispatch only.
  //
  // Structured-readout fallback (search candidates, clarification, raw
  // status rows) is unreachable here because every AskResponse becomes a
  // turn and the transcript renders the narrative + clarification +
  // search results from inside the turn block. The full payload is
  // available on response.payload for debug surfaces.
  return (
    <section className={QA_SECTION}>
      <DemoAskTranscriptCard />
      {/* Diagnostic readout — only shown when the latest response has
          neither a narrative nor a registered renderer card, i.e. the
          agent returned status=search / clarification / error without
          synthesis. Keeps the structured readout addressable for those
          paths without nesting it inside the transcript card. */}
      {response && response.status !== 'simulation' && (
        <DiagnosticReadout response={response} />
      )}
    </section>
  );
}


/** Diagnostic readout for non-simulation responses (search, clarification,
 *  error). Lives outside the transcript card because it renders raw
 *  fields rather than a turn-pair. Kept compact. */
function DiagnosticReadout({
  response,
}: {
  response: NonNullable<ReturnType<typeof useDemoAskStore.getState>['response']>;
}) {
  const T = useCardTypography();
  return (
    <div className="w-full space-y-4 mt-4 pt-4 border-t border-border/40">
      <Row label="Status" value={response.status} />
      {response.error && <Row label="Error" value={response.error} tone="error" />}
      {response.clarification && (
        <Row label="Agent says" value={response.clarification} />
      )}
      {response.intent && (
        <>
          <Row label="Intent" value={response.intent.intent} />
          {response.intent.entity_hint && (
            <Row label="Entity hint" value={response.intent.entity_hint} mono />
          )}
          {response.intent.reasoning && (
            <Row label="Reasoning" value={response.intent.reasoning} />
          )}
          <Row label="Confidence" value={response.intent.confidence} />
        </>
      )}
      {response.search_results && response.search_results.length > 0 && (
        <div className="rounded-xl border border-border bg-card/30 px-4 py-3">
          <p className={cn(T.metadata, 'text-muted-foreground mb-2')}>
            Did you mean…
          </p>
          <ul className="space-y-1">
            {response.search_results.slice(0, 5).map((c) => (
              <li key={c.semantic_id} className={cn(T.body, 'text-sm')}>
                <span className="font-mono text-muted-foreground">{c.entity_type}</span>
                {' · '}
                <span className="text-foreground">{c.name}</span>
                {' '}
                <span className="text-muted-foreground text-xs">({c.score.toFixed(2)})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


/** TestRunCanvas — the stacked composition rendered when a test_runner
 *  response is in the store. Extracted as a sub-component so it can
 *  call `useIsFetching` and expose a `data-test-run-status` signal on
 *  its root — Playwright waits on `[data-test-run-status="complete"]`
 *  rather than polling each card. `useIsFetching` returns the count of
 *  in-flight React Query fetches; when it hits 0 every card's BFF call
 *  has settled (and was already dispatched on mount).
 *
 *  See `klyde_testing/docs/canvas_testability.md` for the end-of-run
 *  signal convention.
 */
function TestRunCanvas() {
  const inFlight = useIsFetching();
  const status = inFlight === 0 ? 'complete' : 'loading';
  return (
    <section className={QA_SECTION} data-test-run-status={status}>
      <div className="w-full">
        <CanvasCardShell contentKey="demo_recording"><DemoRecordingCard /></CanvasCardShell>
        <CanvasCardShell contentKey="demo_test_run_header"><DemoTestRunHeaderCard /></CanvasCardShell>
        <CanvasCardShell contentKey="demo_scene_progression"><DemoSceneProgressionCard /></CanvasCardShell>
        <CanvasCardShell contentKey="demo_weights"><DemoWeightsCard /></CanvasCardShell>
        <CanvasCardShell contentKey="demo_rules"><DemoRulesCard /></CanvasCardShell>
        <CanvasCardShell contentKey="demo_frontier"><DemoFrontierCard /></CanvasCardShell>
        <CanvasCardShell contentKey="demo_extracted_lines"><DemoExtractedLinesCard /></CanvasCardShell>
        <CanvasCardShell contentKey="demo_tokenomics"><DemoTokenomicsCard /></CanvasCardShell>
      </div>
    </section>
  );
}


interface RowProps {
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'error';
}

function Row({ label, value, mono, tone }: RowProps) {
  const T = useCardTypography();
  return (
    <div className="flex items-baseline gap-3">
      <span className={cn(T.metadata, 'text-muted-foreground min-w-[6rem]')}>{label}</span>
      <span
        className={cn(
          T.body,
          mono && 'font-mono text-sm',
          tone === 'error' && 'text-destructive',
        )}
      >
        {value}
      </span>
    </div>
  );
}
